import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { User, ArrowRight, Search, Filter } from '../components/icons';
import { getBlogImage, blogImageMap } from '../assets/Blog';
import { API_BASE_URL } from '../lib/api';
import heroImage from '../assets/Lab.jpg';

interface ApiPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  content: string;
  featuredImage?: string | null;
  category?: string | null;
  author?: { firstName?: string; lastName?: string } | null;
  status?: string;
  featured?: boolean;
  publishedAt?: string | null;
  createdAt: string;
}

const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

// Resolve a post image the same way the homepage does, plus /uploads support.
const resolveImage = (post: ApiPost): string => {
  const src = post.featuredImage;
  if (src) {
    if (/^https?:\/\//.test(src)) return src;
    if (src.startsWith('/uploads')) return `${API_ORIGIN}${src}`;
    if (src.startsWith('/images/blog/')) {
      const filename = src.split('/').pop();
      if (filename) {
        const key = filename.replace('.jpg', '').replace(/\s+/g, '-').toLowerCase();
        for (const [k, v] of Object.entries(blogImageMap)) {
          if (k.toLowerCase().includes(key) || key.includes(k.toLowerCase())) return getBlogImage(v);
        }
      }
    }
  }
  const slugKey = blogImageMap[post.slug];
  if (slugKey) return getBlogImage(slugKey);
  return getBlogImage('default');
};

const Blog = () => {
  const [posts, setPosts] = useState<ApiPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 8;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true); setError(false);
        const res = await fetch(`${API_BASE_URL}/blog-working/blog?page=1&limit=100`);
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (active) setPosts((data.posts as ApiPost[]) || []);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Use admin-flagged featured posts; fall back to the newest two if none set.
  // Show all posts in the grid, with admin-flagged featured posts first.
  const regularPosts = [...posts].sort((a, b) => Number(!!b.featured) - Number(!!a.featured));

  const categories = ['All', ...Array.from(new Set(posts.map(p => p.category).filter(Boolean) as string[]))];

  const filteredPosts = regularPosts.filter(post => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = post.title.toLowerCase().includes(q) || (post.excerpt ?? '').toLowerCase().includes(q);
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section data-hero className="relative overflow-hidden bg-[#0D56A6] pt-36 pb-32">
        {/* Background photo (dental lab / digital workflow) */}
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Ivoclar gradient tint keeps the brand feel and white text readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#4e8f2a]/95 via-[#158a83]/90 to-[#0D56A6]/90" />
        <div className="relative container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-extrabold mb-5 leading-tight text-white">
              Dental Insights <span className="text-[#8EC641]">&amp; News</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto">
              Stay updated with the latest trends, technologies, and best practices in dentistry.
            </p>
          </motion.div>
        </div>

        {/* Smooth wave transition into the content below */}
        <div className="absolute -bottom-px left-0 right-0 leading-[0] text-white">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full h-[60px] md:h-[90px]" xmlns="http://www.w3.org/2000/svg">
            <path fill="currentColor" d="M0,40 C240,100 480,100 720,60 C960,20 1200,20 1440,60 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {loading && (
        <div className="py-24 flex items-center justify-center">
          <span className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#0D56A6]" />
        </div>
      )}

      {!loading && error && (
        <div className="py-24 text-center">
          <p className="text-gray-500 text-lg">Couldn’t load articles. Please try again later.</p>
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="py-24 text-center">
          <p className="text-gray-500 text-lg">No articles published yet — check back soon.</p>
        </div>
      )}

      {!loading && !error && posts.length > 0 && (
        <>
          {/* Search and Filter */}
          <section className="py-12 bg-gray-50">
            <div className="container-custom">
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="flex-1 relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D56A6] focus:border-transparent"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Filter size={20} className="text-gray-400" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D56A6] focus:border-transparent"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Blog Posts Grid */}
          <section className="py-16 bg-white">
            <div className="container-custom">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">All Articles</h2>
                <p className="text-lg text-gray-600">
                  {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
                </p>
              </div>

              {paginatedPosts.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {paginatedPosts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="bg-white rounded-2xl shadow-sm hover:shadow-lg ring-1 ring-gray-100 transition-all duration-300 overflow-hidden group flex flex-col h-full"
                    >
                      <Link to={`/blog/${post.id}`} className="block aspect-video overflow-hidden">
                        <img
                          src={resolveImage(post)}
                          alt={post.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </Link>
                      <div className="p-5 flex flex-col flex-1">
                        {post.category && (
                          <span className="self-start px-2.5 py-1 bg-[#0D56A6]/10 text-[#0D56A6] text-xs font-semibold rounded-full mb-3">
                            {post.category}
                          </span>
                        )}
                        <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug line-clamp-2">
                          <Link to={`/blog/${post.id}`} className="group-hover:text-[#0D56A6] transition-colors">
                            {post.title}
                          </Link>
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>

                        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap truncate">
                            <User size={13} /> {post.author?.firstName || 'DentoPharma'}
                          </span>
                          <Link
                            to={`/blog/${post.id}`}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-[#0D56A6] hover:text-[#8EC641] transition-colors whitespace-nowrap"
                          >
                            Read More
                            <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-12">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg ${
                          currentPage === page
                            ? 'bg-[#0D56A6] text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Blog;
